import React, { useState, useEffect } from 'react';

// Comps
import { Image as ResponsiveImage } from '@arctheme-components/elements/Image/Image';
import Paragraph from '@arctheme-components/elements/Paragraph/Paragraph';
import { Headings } from '@arctheme-components/elements/Headings/Headings';
import { Button } from '@src/stories/elements/Button/Button';

// Hooks
import { useCurrentTextStyle } from '@src/hooks/useCurrentTextStyle';

// Types
import type { Order, OrdersLocales, Settings, OrderLineItem } from './types';

// Shopify helpers
import shopify from '../../../../../js/shopify';
import helpers from 'frontend/js/helpers';

interface AccountOrdersProps {
  orders: Order[];
  locales: OrdersLocales;
  settings: Settings;
  onClickOrder: (event: React.ClickEvent<HTMLLinkElement>, order: Order) => void;
}

const AccountOrders: React.FC<AccountOrdersProps> = ({ orders, locales, settings, onClickOrder }) => {
  const [enrichedOrders, setEnrichedOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getCurrentTextStyle } = useCurrentTextStyle();

  // Use enriched orders if available, fallback to original orders
  const ordersToDisplay: Order[] =
    enrichedOrders.length > 0 ? enrichedOrders : orders && Array.isArray(orders) && orders.length > 0 ? orders : [];

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const currentYear = new Date().getFullYear();
      let result = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      if (result.includes(currentYear.toString())) {
        result = result.replace(`, ${currentYear.toString()}`, '');
      }

      return result;
    } catch {
      return dateString;
    }
  };

  const handleClickBuyAgain = (order: Order) => {
    const detail = order.lineItems.map((item: OrderLineItem) => ({
      variantId: item.variantId,
      quantity: item.quantity
    }));

    if ((window as any).triggerAction) {
      (window as any).triggerAction('runAddToCartAndCartOpen', detail);
    }
  };

  // Helper function to get responsive text styles using the hook
  const getResponsiveTextStyle = (styleKey: string) => {
    const styles = settings?.textStyles?.[styleKey];
    if (!styles) return '';

    return getCurrentTextStyle(styles.default, styles.enableResponsive, styles.mobile, styles.tablet, styles.ultrawide);
  };

  return (
    <>
      <div className={'account-orders-header flex items-center justify-center mb-12 w-full'}>
        <Headings
          type={'h2'}
          text={settings?.texts?.header || 'My Orders'}
          classes={`flex justify-center items-center ${getResponsiveTextStyle('header')}`}
        />
      </div>
      <div className='account-orders w-full grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
        {isLoading && (
          <div className='text-center py-8'>
            <p className='text-sm text-gray-500'>Loading product images...</p>
          </div>
        )}

        {ordersToDisplay.length > 0 ? (
          ordersToDisplay.map((order: Order, index: number) => {
            const { orderName, createdAt, fulfillmentStatus, lineItems, total, billingAddress } = order;
            const itemCount = lineItems.length;

            let displayItems: OrderLineItem[] = [];
            let remainingCount = 0;

            if (itemCount === 1) {
              displayItems = lineItems.slice(0, 1);
              remainingCount = 0;
            } else if (itemCount === 2) {
              displayItems = lineItems.slice(0, 2);
              remainingCount = 0;
            } else if (itemCount === 3) {
              displayItems = lineItems.slice(0, 3);
              remainingCount = 0;
            } else if (itemCount > 3) {
              displayItems = lineItems.slice(0, 3);
              remainingCount = itemCount - 3;
            }

            return (
              <div
                key={orderName}
                className='bg-white rounded-2xl px-2 lg:px-4 py-4 lg:py-6 flex flex-col'
              >
                {/* Order Header */}
                <div className='flex justify-between items-start mb-2'>
                  <div className='space-y-1 w-full'>
                    <div className='flex justify-between items-center gap-2 bg-[#F5F5F5] w-full rounded-[8px] p-[16px]'>
                      <span className={`inline-flex items-center ${getResponsiveTextStyle('statusCard')}`}>
                        {helpers.capitalizeFirst(fulfillmentStatus)}
                      </span>
                      {createdAt && (
                        <span className={`${getResponsiveTextStyle('dateCard')} text-[#878787]`}>
                          {settings?.texts?.updated || 'Updated'} {formatDate(createdAt)}
                        </span>
                      )}
                    </div>
                   
                  </div>
            
                </div>

                {/* Product Images */}
                {displayItems.length > 0 && (
                  <div className='w-full mb-4 lg:mb-5'>
                    <div className='grid grid-cols-2 grid-rows-2 gap-2'>
                      {displayItems.map((item: OrderLineItem, itemIndex: number) => {
                        if (!item.product) return;

                        // Enhanced image URL detection with Storefront API enriched data
                        const imageUrl = item.product.featured_image;

                        // Dynamic grid positioning based on item count
                        let gridClasses = 'overflow-hidden';

                        if (itemCount === 1) {
                          // Single item: 2 columns, 2 rows
                          gridClasses = 'aspect-[0.9] col-span-2 row-span-2 overflow-hidden';
                        } else if (itemCount === 2) {
                          // Two items: each takes 1 column, 2 rows
                          gridClasses = 'aspect-[1.86] col-span-2 row-span-1 overflow-hidden';
                        } else if (itemCount === 3) {
                          // Three items: first two take 1 col/1 row, third takes 2 cols/1 row
                          if (itemIndex < 2) {
                            gridClasses = 'aspect-[0.9] col-span-1 row-span-1 overflow-hidden';
                          } else {
                            gridClasses = 'aspect-[1.86] col-span-2 row-span-1 overflow-hidden';
                          }
                        } else {
                          // More than 3: all take 1 col/1 row
                          gridClasses = 'aspect-[0.9] col-span-1 row-span-1 overflow-hidden';
                        }

                        return (
                          <div
                            key={itemIndex}
                            className={`${gridClasses} bg-[#E6E6E6]`}
                          >
                            {imageUrl && (
                              <ResponsiveImage
                                imageUrl={imageUrl}
                                alt={item.product.title}
                                classes='w-full h-full object-contain'
                                width='64'
                                height='64'
                              />
                            )}
                          </div>
                        );
                      })}

                      {remainingCount > 0 && (
                        <div className='col-span-1 row-span-1 flex items-center justify-center'>
                          <span className='b1--bold'>+{remainingCount}</span>
                        </div>
                      )}
                    </div>
                 
                  </div>
                )}

                <div className={getResponsiveTextStyle('items')}>
                  {itemCount} {settings?.texts?.items || 'Items'}
                </div>

                <button
                  type='button'
                  className={`${getResponsiveTextStyle(
                    'orderNumber'
                  )} text-[#808080] inline-block mb-3 lg:mb-4 text-left`}
                  onClick={(e) => onClickOrder(e, order)}
                >
                  {settings?.texts?.order || 'Order'} #{orderName}
                </button>

                <div
                  className={`${getResponsiveTextStyle('address')} mb-3 lg:mb-4`}
                  dangerouslySetInnerHTML={{ __html: billingAddress }}
                />

                <div className={`${getResponsiveTextStyle('total')} mb-4 lg:mb-6`}>{total}</div>

                {/* Action Buttons */}
                <div className='flex gap-2 mt-auto'>
                  <Button
                    className='button button-outline w-full'
                    onClick={() => handleClickBuyAgain(order)}
                    label={settings?.texts?.buyAgainButton || 'Buy again'}
                    style={settings?.buttons?.buyAgain?.style || 'outline'}
                    size={settings?.buttons?.buyAgain?.size || 'md'}
                  />
                  <Button
                    className='button button-secondary w-full'
                    onClick={(e) => {
                      e.preventDefault();
                      onClickOrder(e as React.ClickEvent<HTMLLinkElement>, order);
                    }}
                    label={settings?.texts?.manageOrderButton || 'Manage Order'}
                    style={settings?.buttons?.manageOrder?.style || 'secondary'}
                    size={settings?.buttons?.manageOrder?.size || 'md'}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div className='col-span-3 text-center py-12'>
            <Paragraph
              type='b2 text-center'
              text={settings?.texts?.noOrdersHeading || locales?.empty || 'No orders found'}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AccountOrders;
